import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import type { ShareDirection, SharedObjectListResponse } from '../../types/api';

// STD-015: server state for the shared-object READ surface (#1205).
// apiService is imported here (a *.queries.ts file) per STD-016.

export interface SharedObjectListFilters {
  /** Required — the endpoint has no "both" mode. See `fileObjects.ts`. */
  direction: ShareDirection;
  /** The other organization: the owner inbound, the grantee outbound. */
  counterpartyOrganizationId?: string;
  limit?: number;
  offset?: number;
}

/**
 * A namespace of its own, NOT a branch under `objectSharesKeys`.
 *
 * The two are different questions over different endpoints — share rows versus
 * the objects those rows reach — and one folder grant changes this listing by
 * every file under it while adding a single row to the other. Sharing a key
 * prefix would be convenient exactly once (invalidation) and wrong thereafter.
 *
 * Granting or revoking DOES change reach, so the mutations invalidate both
 * namespaces explicitly; see `objectShares.mutations.ts`.
 */
export const sharedObjectsKeys = {
  all: ['shared-objects'] as const,
  list: (filters: SharedObjectListFilters) => [...sharedObjectsKeys.all, 'list', filters] as const,
};

/**
 * The objects another organization made reachable to the caller's — or, with
 * `direction: 'outbound'`, the grants the caller's org made.
 *
 * Returns the whole envelope rather than just `objects`, because `total` is not
 * derivable from a page: `objects.length` is at most `limit`, so a view that
 * unwrapped the envelope could only ever draw a pager over the page it is
 * already showing.
 *
 * **Not role-gated, and that is load-bearing.** This is the query a plain member
 * of a grantee organization must be able to run — see `canViewSharedObjects` in
 * `shared-view/sharedViewModel.ts` for why gating it on admin is the #1206 bug.
 */
export function useSharedObjectsQuery(filters: SharedObjectListFilters, enabled = true) {
  return useQuery<SharedObjectListResponse, Error>({
    queryKey: sharedObjectsKeys.list(filters),
    enabled,
    // The view renders failures inline and keeps them on screen above the list
    // they replace. Leaving the global toast on would report the same failure
    // twice, and the toast would vanish while the empty list stayed.
    meta: { skipGlobalErrorToast: true },
    queryFn: async () => apiService.listSharedObjects(filters),
  });
}
